import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getTrackerRulesApi, updateTrackerRulesApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, ErrorBox, InfoHint, Loading, PageTitle, Panel, Pill } from "./cwUi";

/**
 * P1.8 / P5.4 — admin "Check-in rules" for one journey: check-in TIME only.
 * (Nov 2026: late submit + day change moved to Check-in settings, one
 * place for all journeys — shown here read-only with a link.)
 * NEW path /trackers/:id/check-in-rules (button "Check-in rules" added on the
 * journey page next to Edit). Nothing here existed before; the journey
 * editor is unchanged. Coaches can read these rules but not change them.
 *
 *  - Check-in opens at (time + time zone)         (empty = midnight, as before)
 */

const ZONES = [
  "Europe/Dublin",
  "Europe/London",
  "Europe/Paris",
  "Asia/Karachi",
  "Asia/Dubai",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Australia/Sydney",
  "UTC",
];

function RuleRow({ title, hint, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        py: 2,
        borderTop: `1px solid ${CW.line}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: CW.muted, mt: 0.25 }}>{hint}</Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>{children}</Box>
    </Box>
  );
}

export default function CheckInRulesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const result = await getTrackerRulesApi(id);
    if (result?.code === 200) {
      const r = result.rules || result;
      setRules(r);
      setForm({
        checkInTime: r.checkInTime || "",
        checkInTimeZone: r.checkInTimeZone || "Europe/Dublin",
      });
    } else setError(result?.message || "Could not load the rules");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    // Send only what changed (a journey without check-in setup has no time).
    const body = {};
    if (form.checkInTime !== (rules.checkInTime || "")) body.checkInTime = form.checkInTime;
    if (form.checkInTimeZone !== (rules.checkInTimeZone || "Europe/Dublin")) body.checkInTimeZone = form.checkInTimeZone;
    const result = await updateTrackerRulesApi(id, body);
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Check-in time saved", { variant: "success" });
      load();
    } else enqueueSnackbar(result?.message || "Could not save", { variant: "error" });
  };

  if (error && !rules) return <ErrorBox message={error} onRetry={load} />;
  if (!rules || !form) return <Loading />;

  const changed =
    form.checkInTime !== (rules.checkInTime || "") ||
    form.checkInTimeZone !== (rules.checkInTimeZone || "Europe/Dublin");

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 900, mx: "auto", pb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton onClick={() => navigate(`/trackers/${id}`)} aria-label="Back to journey">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <PageTitle>
              Check-in rules
              <InfoHint id="rules.page" />
            </PageTitle>
            <Typography sx={{ fontSize: 13.5, color: CW.muted }}>
              {rules.trackerName} · {rules.cadenceLabel || "No repeating check-in"}
            </Typography>
          </Box>
        </Box>

        <Panel sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>For all journeys</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.75 }}>
                <Pill tone={rules.allowMissedCheckIns ? "green" : "muted"}>
                  Late submit {rules.allowMissedCheckIns ? "on" : "off"}
                </Pill>
                <Pill tone={rules.allowCheckInDayChange ? "green" : "muted"}>
                  Day change {rules.allowCheckInDayChange ? "on" : "off"}
                </Pill>
              </Box>
            </Box>
            <button type="button" className="small-contained-outlined-button" onClick={() => navigate("/check-ins/settings")}>
              Check-in settings
            </button>
          </Box>
        </Panel>

        <Panel>
          <RuleRow
            title="Check-in opens at"
            hint={
              form.checkInTime
                ? `Each check-in opens at ${form.checkInTime} (${form.checkInTimeZone}) on its day and closes at the same time when the next opens.`
                : "Empty: check-ins open at the start of the day (as today)."
            }
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                type="time"
                label="Time"
                value={form.checkInTime}
                onChange={(e) => setForm({ ...form, checkInTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130 }}
              />
              <TextField
                select
                size="small"
                label="Time zone"
                value={form.checkInTimeZone}
                onChange={(e) => setForm({ ...form, checkInTimeZone: e.target.value })}
                sx={{ width: 190 }}
              >
                {(ZONES.includes(form.checkInTimeZone) ? ZONES : [form.checkInTimeZone, ...ZONES]).map((z) => (
                  <MenuItem key={z} value={z}>
                    {z}
                  </MenuItem>
                ))}
              </TextField>
              {form.checkInTime ? (
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() => setForm({ ...form, checkInTime: "" })}
                >
                  Clear
                </button>
              ) : null}
            </Box>
          </RuleRow>
          {changed ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Changing the opening time moves every member's open and upcoming check-in windows on this journey. Submitted check-ins are not changed.
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
            <button type="button" className="small-contained-outlined-button" disabled={busy || !changed} onClick={load}>
              Reset
            </button>
            <button type="button" className="small-contained-button" disabled={busy || !changed} onClick={save}>
              Save time
            </button>
          </Box>
        </Panel>

        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <button type="button" className="small-contained-outlined-button" onClick={() => navigate(`/check-ins/planner?trackerId=${id}`)}>
            Open check-in planner
          </button>
          <button type="button" className="small-contained-outlined-button" onClick={() => navigate(`/check-ins/${id}/questions`)}>
            Check-in questions
          </button>
        </Box>
      </Box>
    </div>
  );
}
